import React, { useEffect, useRef, useState } from 'react';
import { Breadcrumb, Input, Header, Dimmer, Button, Modal } from 'semantic-ui-react';
import { useHistory, useParams } from 'react-router-dom';
import HorizontalCards from '../HorizontalCards/HorizontalCards';
import Gallery from 'react-photo-gallery';
import InfiniteScroller from 'react-infinite-scroller';
import TemplateItem from './TemplateItem';
import { useSelector, useDispatch } from 'react-redux';
import { designActions, templateActions } from '../../actions';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import './TemplateList.css';
import TemplatePreview from '../TemplatePreview/TemplatePreview';

const TemplateList = () => {
  const { t } = useTranslation();
  const { _type, _query } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(state => state.authentication.user);
  const hasSubscription = useSelector(state => state.subscription.hasSubscription);
  const loading = useSelector(state => state.template.status);
  const templates = useSelector(state => state.template.templates);
  const hasMoreTemplate = useSelector(state => state.template.hasMoreTemplate);
  const tagsItems = {
    autumnTag: useSelector(state => state.template.autumnTag),
    birthdayTag: useSelector(state => state.template.birthdayTag),
    foodTag: useSelector(state => state.template.foodTag),
    travelTag: useSelector(state => state.template.travelTag),
    saleTag: useSelector(state => state.template.saleTag),
    summerTag: useSelector(state => state.template.summerTag),
  };
  const [activeDim, setActiveDim] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const selectedTemplateRef = useRef();
  const inputRef = useRef();
  const initRef = useRef(true);
  const categoryRef = useRef();
  const sections = [
    { key: 'home', content: t('home'), link: true, onClick: () => history.push('/home') },
    {
      key: 'templates',
      content: t('templates'),
      link: true,
      onClick: () => history.push('/home/templates'),
    },
    { key: _query, content: t(_query), active: true },
  ];
  const tags =
    _type === 'categories' && _query === 'Instagram Stories'
      ? ['ad', 'get to know me', 'love', 'travel', 'food', 'summer']
      : ['autumn', 'birthday', 'food', 'love', 'nowruz', 'quote', 'sale', 'summer', 'travel', 'winter'];

  useEffect(() => {
    if (_type === 'categories') categoryRef.current = _query;
    inputRef.current.value = _query;
    dispatch(templateActions.search({ q: _query }, true));
    if (_type === '___') {
      tags.forEach(tag => {
        dispatch(templateActions.search({ q: tag, categories: _query }, true, `${tag}Tag`));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initRef.current) {
      return;
    }
    dispatch(templateActions.search({ q: _query, categories: categoryRef.current }, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_type, _query]);

  useEffect(() => {
    if (initRef.current) {
      initRef.current = false;
      return;
    }
    const mainElement = document.getElementsByClassName('main')[0];
    if (mainElement.clientHeight === mainElement.scrollHeight && hasMoreTemplate && loading !== 'loading') {
      if (_type === 'categories') dispatch(templateActions.search({ q: _query }, false));
      else dispatch(templateActions.search({ q: _query, categories: categoryRef.current }, false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const customInfiniteScroller = (initialLoad, hasMore, type, photos ,showNotFound = true) => {
    return (
      <InfiniteScroller
        pageStart={0}
        threshold={100}
        initialLoad={false}
        hasMore={hasMore}
        loadMore={() => {
          loading !== 'loading' && dispatch(templateActions.search({ q: _query }, false));
        }}
        useWindow={false}
        getScrollParent={() => document.getElementsByClassName('main')[0]}
      >
        {photos.length === 0 && <span>{t('itemNotFound')}</span>}
        <Gallery photos={photos} targetRowHeight={200} renderImage={customRenderImage} />
      </InfiniteScroller>
    );
  };

  // eslint-disable-next-line react/prop-types, no-unused-vars
  const customRenderImage = ({ index, left, top, key, photo }) => {
    const imgStyle = {
      transition: 'transform .135s cubic-bezier(0.0,0.0,0.2,1),opacity linear .15s',
    };
    const cont = {
      marginTop: '-2px',
      cursor: 'pointer',
      overflow: 'hidden',
      position: 'relative',
    };
    return (
      <TemplateItem
        key={key}
        index={index}
        photo={photo}
        imgStyle={imgStyle}
        cont={cont}
        onClick={handleTemplateClicked}
      />
    );
  };

  const handleTemplateClicked = template => {
    if (_.get(template, 'data.pages', []).length > 1) {
      selectedTemplateRef.current = template;
      setShowPreview(true);
    } else {
      // Block action if template has price and user hadn't subscription
      if (user.role === 'client' && _.get(template, 'data.price', 0) > 0 && !hasSubscription) {
        setActiveDim(true);
        return;
      }

      dispatch(
        designActions.create({
          templateId: _.get(template, 'data.id'),
          templateCategory: _.get(template, 'data.categories', [''])[0],
        }),
      );
    }
  };

  const handleSearchKeyPress = event => {
    if (event.key === 'Enter') {
      dispatch(templateActions.search({ q: inputRef.current.value }, true));
      history.push(`/home/templates/tags/${inputRef.current.value}`);
    }
  };

  return (
    <div className="templateList">
      <div className="header mainHeader">
        <Input
          className="searchField"
          icon="search"
          placeholder={`${t('search')}...`}
          onKeyPress={handleSearchKeyPress}
        >
          <input ref={inputRef} />
        </Input>
      </div>
      <Breadcrumb icon="left angle" sections={sections} />
      {_type === 'categories' && (
        <div className="tagListBox">
          {tags.map(tag => (
            <a
              key={tag}
              className="tagBtn"
              href={`/home/templates/tags/${tag}`}
              onClick={event => {
                event.preventDefault();
                history.push(`/home/templates/tags/${tag}`);
              }}
            >
              {t(`tags:${tag}`)}
            </a>
          ))}
        </div>
      )}
      <Header className="templatesTitle" content={t(_query)} />
      <div className="content">
        {_type === '___' &&
          tags.map((tag, index) => (
            <HorizontalCards
              key={index}
              parent="Startup"
              title={t(`tags:${tag}`)}
              showMore={t('showMore')}
              itemType="templates"
              items={tagsItems[`${tag}Tag`]}
              height={150}
              onShowMoreClick={() => history.push(`/home/templates/tags/${tag}`)}
              onItemSelect={template => handleTemplateClicked(JSON.parse(template))}
            />
          ))}
        {customInfiniteScroller(true, hasMoreTemplate, 'Template', templates)}
      </div>
      <Dimmer active={activeDim} blurring="true" className="attentionAlert">
        <Header as="h2" icon>
          {t('noSubscription')}
          <br />
        </Header>
        <div className="dimContent">
          <Button className="vButton colorPrimary" content={t('gotIt')} onClick={() => setActiveDim(false)} />
        </div>
      </Dimmer>

      <Modal size="large" open={showPreview} className="templatePreview" onClose={() => setShowPreview(false)}>
        <TemplatePreview template={selectedTemplateRef.current} />
      </Modal>
    </div>
  );
};

export default TemplateList;
